import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Loader2, Save, BookOpen } from "lucide-react";
import { useAuth } from "../lib/auth";

export default function Rulebook() {
  const { user } = useAuth();
  const [rb, setRb] = useState(null);
  const [factors, setFactors] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/rulebook").then((r) => setRb(r.data));
    api.get("/emission-factors").then((r) => setFactors(r.data));
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put("/rulebook", rb);
      setRb(r.data);
      toast.success(`Rulebook saved (v${r.data.version})`);
    } catch { toast.error("Save failed"); }
    finally { setBusy(false); }
  };

  if (!rb) return <div className="text-slate-500">Loading…</div>;

  const editable = ["admin","sustainability"].includes(user?.role);

  const F = ({ k, label, type = "text" }) => (
    <div>
      <label className="overline text-slate-500 block mb-1">{label}</label>
      <input
        data-testid={`rb-${k}`}
        type={type}
        value={rb[k] ?? ""}
        disabled={!editable}
        onChange={(e) => setRb({ ...rb, [k]: e.target.value })}
        className="w-full px-3 py-2 border border-slate-300 text-sm font-mono outline-none focus:border-emerald-700 disabled:bg-slate-50"
      />
    </div>
  );

  return (
    <div data-testid="rulebook-page" className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline text-emerald-800 flex items-center gap-2"><BookOpen className="w-3 h-3" /> Methodology</div>
          <h1 className="font-heading text-4xl font-extrabold tracking-tight text-slate-900 mt-1">Currency PCF Rulebook</h1>
          <p className="text-sm text-slate-600 mt-2">Version-controlled methodology aligned with ISO 14067 and the GHG Protocol Product Standard.</p>
        </div>
        {editable && (
          <button data-testid="rb-save" onClick={save} disabled={busy} className="inline-flex items-center gap-2 px-5 py-3 text-xs uppercase tracking-[0.2em] bg-emerald-800 text-white hover:bg-emerald-900">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save Rulebook (v{(rb.version||0) + 1})
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <F k="standard" label="Standard" />
        <F k="functional_unit" label="Functional Unit" />
        <F k="system_boundary" label="System Boundary" />
        <F k="allocation_method" label="Allocation Method" />
        <F k="spoilage_rule" label="Spoilage Rule" />
        <F k="end_of_life_default" label="End-of-Life Default" />
      </div>

      <div className="border border-slate-200 p-6 bg-white">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="overline text-emerald-800">Emission Factor Library</div>
            <div className="font-heading text-xl font-bold text-slate-900">{factors.length} factors</div>
          </div>
          <span className="text-xs text-slate-500 font-mono">Version {rb.version}</span>
        </div>
        <table data-testid="factors-table" className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left">
              <th className="px-3 py-2 overline text-slate-500">Name</th>
              <th className="px-3 py-2 overline text-slate-500">Category</th>
              <th className="px-3 py-2 overline text-slate-500">Unit</th>
              <th className="px-3 py-2 overline text-slate-500 text-right">Value</th>
              <th className="px-3 py-2 overline text-slate-500">Confidence</th>
              <th className="px-3 py-2 overline text-slate-500">Source</th>
            </tr>
          </thead>
          <tbody>
            {factors.map((f, i) => (
              <tr key={f.id} className={`${i%2 ? "bg-slate-50/50" : ""} border-b border-slate-100`}>
                <td className="px-3 py-2 font-mono text-slate-900">{f.name}</td>
                <td className="px-3 py-2">{f.category}</td>
                <td className="px-3 py-2 font-mono text-xs">{f.unit}</td>
                <td className="px-3 py-2 text-right font-mono">{f.value}</td>
                <td className="px-3 py-2"><span className={`text-[10px] uppercase tracking-[0.18em] ${f.confidence==='high'?'text-emerald-800':f.confidence==='medium'?'text-amber-700':'text-red-700'}`}>{f.confidence}</span></td>
                <td className="px-3 py-2 text-xs text-slate-500">{f.source}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
